"use client";

import React, { useState, useEffect } from "react";
import { Trash2, Loader2, Package, Clock, RotateCcw } from "lucide-react";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  CATALOGUES_CACHE_KEY,
  CatalogueService,
  type Catalogue,
} from "@/services/catalogue.service";
import { languageName } from "@/constants/languages";
import { AlertMessage } from "@/components/ui/alert-message";
import { CreateCatalogueButton } from "@/components/catalog/create-catalogue-button";


interface EmptyStateProps {
  onCreateCatalogue?: () => void;
  showCTA?: boolean;
  message?: string;
}

function EmptyState({ onCreateCatalogue, showCTA = true, message }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-6">
      <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-6 rounded-full mb-6">
        <Package className="h-12 w-12 text-purple-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-3">
        {showCTA ? "No catalogues yet" : message || "No catalogues found"}
      </h3>
      <p className="text-gray-600 max-w-md mx-auto mb-8 text-center">
        {showCTA 
          ? "Create your first catalogue to organize and manage your products effectively. Catalogues help you group related items and streamline your inventory management."
          : message || "There are no catalogues in this section."
        }
      </p>
      {showCTA && (
        <CreateCatalogueButton 
          onCatalogueCreated={onCreateCatalogue}
        />
      )}
    </div>
  );
}

interface CatalogueListProps {
  onViewCatalog: (catalogueId: string) => void;
  onCatalogueCountChange?: (count: number) => void;
}

// SWR fetcher for catalogues
const fetchCatalogues = async () => {
  return await CatalogueService.getAll();
};

export function CatalogueList({ onViewCatalog, onCatalogueCountChange }: CatalogueListProps) {
  const [activeTab, setActiveTab] = useState<"active" | "deleted">("active");
  const [actionError, setActionError] = useState<string | null>(null);
  
  // Use SWR for data fetching with caching
  const { data: catalogues = [], error, isLoading, mutate } = useSWR<Catalogue[]>(
    CATALOGUES_CACHE_KEY,
    fetchCatalogues,
    {
      revalidateOnFocus: false,
      refreshInterval: (latest) => latest?.some((catalogue) => catalogue.provisioningStatus === "provisioning") ? 5_000 : 0,
      refreshWhenHidden: false,
    }
  );

  // Notify parent component when catalogue count changes
  useEffect(() => {
    onCatalogueCountChange?.(catalogues.length);
  }, [catalogues, onCatalogueCountChange]);

  const handleDeleteCatalogue = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    if (
      !window.confirm(
        "Are you sure you want to delete this catalogue?\n\n" +
          "All of its products and data will be permanently deleted after 30 days. " +
          "Until then you can restore it from the Deleted tab."
      )
    )
      return;

    try {
      setActionError(null);
      await CatalogueService.delete(id);
      // Revalidate the cache to refresh the list
      await mutate();
    } catch (err: any) {
      setActionError(err.message || "Failed to delete catalogue");
    }
  };

  const handleToggleEnabled = async (id: string) => {
    try {
      setActionError(null);
      // Call the API to toggle the enabled status
      await CatalogueService.toggleEnabled(id);
      // Revalidate the cache to refresh the list
      await mutate();
    } catch (err: any) {
      setActionError(err.message || "Failed to toggle catalogue status");
    }
  };

  const handleRevertDeletion = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setActionError(null);
      await CatalogueService.restore(id);
      // Revalidate the cache to refresh the list
      await mutate();
    } catch (err: any) {
      setActionError(err.message || "Failed to restore catalogue");
    }
  };

  // Filter catalogues based on active tab
  const filteredCatalogues = catalogues.filter((catalogue) => {
    if (activeTab === "active") {
      return !catalogue.deletedAt;
    } else {
      return catalogue.deletedAt;
    }
  });

  // Calculate days until permanent deletion
  const getDaysUntilDeletion = (deletedAt: string): number => {
    const deleted = new Date(deletedAt);
    const permanentDeleteDate = new Date(deleted.getTime() + 30 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = permanentDeleteDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <div className="space-y-6">
      <AlertMessage message={error} type="error" />
      <AlertMessage message={actionError} type="error" />

      {/* Tabs for Active/Deleted Catalogues */}
      <div className="flex gap-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("active")}
          className={`py-2 px-1 border-b-2 font-medium text-sm ${
            activeTab === "active"
              ? "border-pink-500 text-pink-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          Active Catalogues
        </button>
        <button
          onClick={() => setActiveTab("deleted")}
          className={`py-2 px-1 border-b-2 font-medium text-sm ${
            activeTab === "deleted"
              ? "border-pink-500 text-pink-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          Deleted Catalogues
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2">Loading catalogues...</span>
        </div>
      ) : catalogues.length === 0 ? (
        // No catalogues at all - show CTA
        <EmptyState onCreateCatalogue={() => mutate()} showCTA={true} />
      ) : filteredCatalogues.length === 0 ? (
        // Has catalogues but none in current tab - show message without CTA
        <EmptyState 
          onCreateCatalogue={() => mutate()} 
          showCTA={false} 
          message={activeTab === "active" ? "No active catalogues" : "No deleted catalogues"}
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Enabled</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Processing</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Languages</TableHead>
                <TableHead>Created At</TableHead>
                {activeTab === "deleted" && (
                  <TableHead>Deletion In</TableHead>
                )}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCatalogues.map((catalogue) => (
                <TableRow 
                  key={catalogue.id} 
                  className={catalogue.provisioningStatus === "ready"
                    ? "cursor-pointer transition-colors hover:bg-muted/50"
                    : "cursor-default opacity-75"}
                  onClick={() => {
                    if (catalogue.provisioningStatus === "ready") onViewCatalog(catalogue.id);
                  }}
                >
                  <TableCell className="pl-6">
                    <Switch 
                      checked={catalogue.enabled}
                      onCheckedChange={() => handleToggleEnabled(catalogue.id)}
                      aria-label={`Toggle ${catalogue.name} catalogue`}
                      disabled={!!catalogue.deletedAt || catalogue.provisioningStatus !== "ready"}
                      className={
                        catalogue.deletedAt || catalogue.provisioningStatus !== "ready"
                          ? "opacity-50 grayscale"
                          : ""
                      }
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{catalogue.name}</span>
                      {catalogue.provisioningStatus === "provisioning" ? (
                        <Badge variant="secondary">Preparing</Badge>
                      ) : catalogue.provisioningStatus === "failed" ? (
                        <Badge variant="destructive">Provisioning failed</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {Object.values(catalogue.processingFeatures).filter(Boolean).length} enabled
                    </Badge>
                  </TableCell>
                  <TableCell>{catalogue.description || "-"}</TableCell>
                  <TableCell>
                    <div className="flex max-w-xs flex-wrap gap-1">
                      {catalogue.languages.map((language) => (
                        <Badge
                          key={language}
                          variant="secondary"
                          title={languageName(language)}
                          className="uppercase"
                        >
                          {language}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(catalogue.createdAt).toLocaleDateString()}
                  </TableCell>
                  {activeTab === "deleted" && catalogue.deletedAt && (
                    <TableCell>
                      <div className="flex items-center gap-2 text-amber-600">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm">{getDaysUntilDeletion(catalogue.deletedAt)} days</span>
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {activeTab === "deleted" && catalogue.provisioningStatus === "ready" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-500 hover:text-green-700 hover:bg-green-50"
                          onClick={(e) => handleRevertDeletion(catalogue.id, e)}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleDeleteCatalogue(catalogue.id, e)}
                        disabled={catalogue.provisioningStatus === "provisioning"}
                        title={catalogue.provisioningStatus === "provisioning" ? "Wait for catalogue preparation to finish" : undefined}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
